// Updated job-listing.component.ts
import { AsyncPipe, isPlatformBrowser, NgClass, NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, Input, makeStateKey, OnDestroy, OnInit, PLATFORM_ID, TransferState } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_MENU_SCROLL_STRATEGY, MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { SelectTypeaheadDirective } from '../../../directives/select-typeahead.directive';
import { Overlay } from '@angular/cdk/overlay';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterModule } from '@angular/router';
import { BehaviorSubject, debounceTime, Subscription, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { customDebounceTime, GigsumoConstants } from '../../../../helpers/gigsumoConstants';
import { jobModuleConfig } from '../../../../helpers/jobModule.config';
import { DataTransferService } from '../../../../service/data-transfer.service';
import { GigsumoService } from '../../../../service/gigsumo.service';
import { SiteMapService } from '../../../../service/seoService';
import { digitKeyOnly } from '../../../app.component';
import { CustomPaginationComponent, PaginationConfig } from '../../../paginators/custom-pagination/custom-pagination.component';
import { SkeletonLoadersComponent } from '../../skeleton-loaders/skeleton-loaders.component';
import { JobCardComponent, JobProvider } from '../job-card/job-card.component';
import { JobDetailsComponent } from '../job-details/job-details.component';
import { JobSidebarComponent } from '../job-sidebar/job-sidebar.component';
import { SvgTemplatesComponent } from '../svg-templates/svg-templates.component';
// import { CustomPaginationComponent, PaginationConfig } from './custom-pagination.component';

// Keep existing helper functions
export function getJobParams(job: any) {
  const ENVIRONMENT = environment;
  let data = {
    jobId: job.jobId,
    tabName: "job-details",
    From: "JOBS_FROM_CONNECTION"
  }
  return `${ENVIRONMENT.careOnlineWebUrl}auth/login?redirectTo=/newjobs&absolutePath=true&data=${encodeURIComponent(JSON.stringify(data))}`;
}

export function getCandidateParams(job: any) {
  const ENVIRONMENT = environment;
  let data = {
    candidateId: job.candidateId,
    tabName: "profileSummary",
    From: "CANDIDATES_FROM_CONNECTION"
  }
  return `${ENVIRONMENT.careOnlineWebUrl}auth/login?redirectTo=/newcandidates&absolutePath=true&data=${encodeURIComponent(JSON.stringify(data))}`;
}

@Component({
  selector: 'app-job-listing',
  imports: [
    NgFor, JobCardComponent, JobDetailsComponent, JobSidebarComponent, SvgTemplatesComponent,
    AsyncPipe, NgIf, NgFor, MatMenuModule, MatSelectModule, SelectTypeaheadDirective, ReactiveFormsModule, NgClass,
    FormsModule, SkeletonLoadersComponent, RouterModule, CustomPaginationComponent
  ],
  standalone: true,
  templateUrl: './job-listing.component.html',
  styleUrl: './job-listing.component.scss',
  providers: [
    {
      provide: MAT_MENU_SCROLL_STRATEGY,
      useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.close(),
      deps: [Overlay]
    }
  ]
})
export class JobListingComponent implements OnDestroy, OnInit, AfterViewInit {
  countryList: any[] = [];
  stateList: any[] = [];

  // Pagination configuration
  paginationConfig: PaginationConfig = {
    currentPage: 1,
    totalItems: 0,
    itemsPerPage: 10,
    // Narrow left column (~320px): keep a small page window. With the
    // compact 32px buttons (matching the wireframe spec), «« · « · N · ... ·
    // last · » · »» fits on one row without clipping.
    maxVisiblePages: 2,
    showFirstLast: true,
    showPrevNext: true,
    baseRoute: './',
    queryParams: {}
  };

  workModeCheckboxes: Array<{ label: string, selected: boolean, disable: boolean }> = [
    { label: 'All', selected: false, disable: false },
    { label: 'Onsite', selected: false, disable: false },
    { label: 'Remote', selected: false, disable: false },
    { label: 'Hybrid', selected: false, disable: false },
  ];

  // SSR-related properties
  private readonly JOBS_LIST_KEY = makeStateKey<any>('SSR_JOBS_LIST');
  private readonly COUNTRIES_KEY = makeStateKey<any>('SSR_COUNTRIES');
  isDataLoaded: boolean = false;
  locationCount: number = 0;
  baseUrl = environment.ssrUrl;

  constructor(
    private dataTransfer: DataTransferService,
    private transferState: TransferState,
    private router: Router,
    public jobData: JobProvider,
    public siteMap: SiteMapService,
    private activatedRoute: ActivatedRoute,
    public gigsumoService: GigsumoService,
    @Inject(PLATFORM_ID) private platformId: object,
    private el: ElementRef
  ) {
    // Only handle navigation events here, not query params
    if (isPlatformBrowser(this.platformId)) {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          // Flag browser Back/Forward (popstate). On popstate we must HONOR the URL —
          // the auto-navigate-to-first-job must NOT run, or it hijacks Back by jumping
          // to the listing's first job instead of the record the user is returning to.
          this.isPopStateNav = event.navigationTrigger === 'popstate';
        }
        if (event instanceof NavigationEnd) {
          window.scrollTo(0, 0);

          // Desktop (>=769px) uses a fixed-height app shell — the page
          // itself doesn't scroll, the middle detail and right sidebar
          // scroll internally (see job-listing.component.scss:382-391).
          // Without this, clicking a different job after scrolling
          // through the previous one keeps the inner scrollTop, so the
          // new job's details open mid-page (user misses the title/CTA
          // and the sidebar widgets stay scrolled to the bottom).
          // window.scrollTo covers the mobile single-scroll layout; this
          // covers the desktop split-scroll layout.
          document.querySelector('.gj-detail-col')?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          document.querySelector('.gj-sidebar-col')?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          // The left column's internal `.gj-jl-cards` scroll is INTENTIONALLY
          // NOT reset here. NavigationEnd fires on every card click too, and
          // resetting the list scroll on a card switch was disorienting —
          // users want to keep their place in the list when peeking at
          // different jobs. List scroll-to-top happens only on "fresh
          // listing data" events (search, filter, reset, pagination, page
          // reload, breadcrumb) — those all funnel through
          // `filterapicallSSR()` or the breadcrumb branch, which handle it
          // themselves.

          // "Find Job" menu click → URL went back to bare /jobs (no path
          // slug, no `searchText`). Reset stale filter-form / search state
          // so the listing returns to its default view.
          //
          // The mirror block inside `queryParams.subscribe()` only fires
          // when queryParams actually CHANGE. Form-only filters never
          // touch the URL — going from `/jobs/foo` to `/jobs` leaves
          // queryParams unchanged at `{}` and the observable stays
          // silent, so that block can't see this case. NavigationEnd
          // fires on every navigation regardless, so this is the
          // reliable hook for the form-only-filters case. (The queryParams
          // block still catches the searchText-was-set case, which DOES
          // change queryParams.)
          if (this.filterForm && !this.isSearching) {
            const navRawJid = this.activatedRoute.snapshot.paramMap.get('jobId');
            const navJobId = (!navRawJid || navRawJid === 'all')
              ? null
              : this.extractJobId(navRawJid);
            const navHasSearchText = !!this.activatedRoute.snapshot.queryParams['searchText'];
            // Pagination URLs (/jobs?page=N) also have no jobId / no searchText,
            // so the old check matched them. Real menu clicks never carry a
            // `page` query param; pagination always does. Excluding page>1
            // keeps the reset off the pagination path regardless of the
            // routerLink-vs-(click) race in custom-pagination.
            const navPageRaw = this.activatedRoute.snapshot.queryParams['page'];
            const navPage = navPageRaw ? parseInt(navPageRaw, 10) : 1;
            if (!navJobId && !navHasSearchText && navPage <= 1) {
              const hasActiveSearch = !!this.searchControl.value;
              const f = this.filterForm.value || {};
              const postedDayActive = f.postedDayRange && f.postedDayRange !== this.POSTED_ANY;
              const experienceActive = f.experienceType && f.experienceType !== this.EXPERIENCE_ANY;
              const locationActive = !!(f.country || f.state || f.city || f.zipCode);
              const classificationActive = Array.isArray(f.jobClassification) && f.jobClassification.length > 0;
              const hasActiveFilters =
                this.engagementTypeCount > 0 ||
                this.workTypeCount > 0 ||
                this.locationCount > 0 ||
                postedDayActive ||
                experienceActive ||
                locationActive ||
                classificationActive;
              // `currentPage > 1` covers the "paginated-but-no-filters" case:
              // user paginated to /jobs?page=2 with nothing else applied, then
              // clicked the menu. URL flips to /jobs (no `?page=`) but the
              // in-memory cursor still says page 2, and without filters/search
              // the reset would otherwise be skipped, leaving the listing
              // stranded on page-2 data with a stale pagination strip.
              if (hasActiveSearch || hasActiveFilters || this.currentPage > 1) {
                console.log('🔄 NavigationEnd → clean /jobs URL with active search/filters/page — resetting');
                this.resetFiltersOnMenuClick();
                return;
              }
            }
          }

          // Browser Back/Forward changes the URL WITHOUT a card click's setData(), so the
          // detail provider can be left pointing at the previously viewed job. NavigationEnd
          // fires on EVERY navigation regardless of the paramMap/queryParams emission order,
          // so it's the reliable place to keep the detail in sync with the URL's job.
          {
            const syncRawJid = this.activatedRoute.snapshot.paramMap.get('jobId');
            const syncJobId = (!syncRawJid || syncRawJid === 'all') ? null : this.extractJobId(syncRawJid);
            const provJobId = this.jobData.getJob?.jobId?.toString().toUpperCase();
            const mismatch = !!(syncJobId && provJobId && provJobId !== syncJobId.toUpperCase());
            // ONLY on browser Back/Forward (popstate). A forward card click already sets the
            // provider via setData(), so this sync is redundant there — and its reload runs
            // filterapicallSSR(), which resets the left list scroll to the top. Firing it on a
            // forward click yanked the list back to the top when the user selected a job lower
            // down. popstate is the only case where the URL changes without a click to sync.
            if (mismatch && this.isPopStateNav) {
              // Back/Forward. Reload the DEFAULT listing (no pin) so the ORIGINAL order is
              // restored — a preceding forward redirect may have pinned a job to the top — and
              // let JobsListdata select the URL's job from it.
              this.jobId = syncJobId;
              this.filterPayLoad.jobsToFilter = null;
              this.filterapicallSSR(this.searchControl.value);
            }
          }

          // Previously: a 2-second setTimeout here flipped `loadAPIcall`
          // back to false on every /jobs NavigationEnd. On rapid breadcrumb
          // / footer clicks, the stale timer from an earlier click would
          // fire mid-flight of a later click's API, turning loadAPIcall off
          // while `response` was still reset to []. The empty-state ngIf
          // (`!loadAPIcall && response.empty && …`) then briefly painted.
          // Removed — `filterapicallSSR` / `handleResponse` already manage
          // loadAPIcall correctly; no fallback timer needed.
        }
      });
    }
  }

  ngAfterViewInit(): void {
    // Custom pagination doesn't require ViewChild setup like Mat-Paginator
    // But we can still handle any custom events if needed
  }

  itemsPerPage = 10;
  isCheck: boolean = true;
  filterForm!: FormGroup;
  currentPage: number = 1;
  routeSub = new Subscription;
  // True while the current navigation is a browser Back/Forward (popstate). Used to
  // suppress the auto-navigate-to-first-job so Back honors the URL.
  isPopStateNav = false;
  jobId: string | null = '';
  searchControl = new FormControl('');

  // Add a flag to track whether the page change is coming from search
  private isSearching = false;

  // One-shot flag — flipped to false at the end of the first
  // queryParams emission. While true, the URL path slug is treated as
  // user intent (fresh navigation from home / external link) and
  // `jobsToFilter` is preserved. After the first emission, the
  // searchText-gated wipe below runs as today so the search-clear
  // flow still drops leftover slugs.
  private isInitialQueryParams = true;

  ngOnInit() {
    // Initialize filter form
    this.initializeFilterForm();

    // Initialize pagination config
    this.updatePaginationConfig();

    // Set up search control value changes
    this.valueChangesSub = this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => {
          console.log('Search control changed:', this.searchControl.value);
          this.response.next([]);

          // Set the searching flag to true
          this.isSearching = true;

          // Reset to first page when searching
          this.currentPage = 1;
          this.updatePaginationConfig();

          // Clear the previously-selected job so the auto-select gate in
          // handleResponse() (`!this.jobId`) opens and the first card of the
          // new result set becomes the active one. Without this, a user
          // searching while a detail is open would keep the stale selection
          // and the right-side panel would stay on a job that isn't in the
          // filtered results.
          this.jobId = null;

          // Update payload first
          const updatedPayload = {
            ...this.filterPayLoad,
            searchAfterKey: null,
            jobsToFilter: null,
            page: 1
          };
          this.filterPayLoad = updatedPayload;

          // Then update URL
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
              page: 1,
              searchText: this.searchControl.value
            },
            queryParamsHandling: 'merge',
          }).then(() => {
            setTimeout(() => {
              this.isSearching = false;
            }, 300);
          });
        })
      )
      .subscribe(searchTerm => this.filterapicallSSR(searchTerm));

    //  FIXED: Get jobId from route parameters with proper extraction
    const rawJobId = this.activatedRoute.snapshot.paramMap.get('jobId');
    console.log('Raw Job ID from URL:', rawJobId);

    // Extract the actual job ID from the complex URL parameter
    if (!rawJobId || rawJobId === 'all') {
      this.jobId = null;
    } else {
      this.jobId = this.extractJobId(rawJobId);
    }

    console.log('Extracted Job ID:', this.jobId); // Add this for debugging

    // Setup initial filter payload with the correct job ID
    const filPayLd = {
      ...this.filterPayLoad,
      jobsToFilter: this.jobId,
      page: this.currentPage,
      limit: this.itemsPerPage
    };
    this.filterPayLoad = filPayLd;
    this.tempFilterPayload = structuredClone(this.filterPayLoad);

    // Set up query parameters handling
    this.handleQueryParams();

    // Check for SSR data
    const hasJobsData = this.checkForSSRJobsData();
    const hasCountriesData = this.checkForSSRCountriesData();

    // Only make API calls if no SSR data was found and no search text in URL
    if (!hasJobsData && !this.activatedRoute.snapshot.queryParams['searchText']) {
      this.filterapicallSSR(this.searchControl.value);
    }

    if (!hasCountriesData) {
      this.getCountries();
    }
  }

  generateJobUrl(jobData: any): string {
    if (!jobData) return '/jobs';

    try {
      // New SEO pattern: /jobs/{title-slug}[-{city}-{state}]-{JID}
      // Slug builder lowercases, hyphenates, and drops missing location parts.
      const slug = this.gigsumoService.buildJobSlug(jobData);
      return slug ? `/jobs/${slug}` : '/jobs';
    } catch (error) {
      console.error('Error generating job URL:', error);
      return jobData.jobId ? `/jobs/${this.gigsumoService.slugify(jobData.jobId)}` : '/jobs';
    }
  }

  private updatePaginationConfig(direction?: 'forward' | 'backward') {
    // Always use the generic route for pagination navigation
    const genericRoute = '/jobs';

    this.paginationConfig = {
      ...this.paginationConfig,
      currentPage: this.currentPage,
      totalItems: this.totalCountJob,
      itemsPerPage: this.itemsPerPage,
      baseRoute: genericRoute, // Use generic route instead of current route
      // Direction is set only on real page navigations (onPageChange).
      // updatePaginationConfig is also called from other paths (search
      // reset, menu-click reset, etc.); leaving direction off there
      // preserves whatever the prior page nav set.
      ...(direction ? { direction } : {}),
      queryParams: {
        // Preserve non-page query params but exclude job-specific ones
        ...Object.fromEntries(
          Object.entries(this.activatedRoute.snapshot.queryParams)
            .filter(([key]) => key !== 'page' && key !== 'jobId')
        )
      }
    };
  }

  private extractJobId(urlJobId: string | null): string | null {
    if (!urlJobId) return null;

    try {
      // New URL pattern slugs are all-lowercase ("…-jid12345"), legacy URLs were
      // mixed case ("JID12345"). Match either, then uppercase the prefix so the
      // result lines up with backend records ("JID12345", "JB-0000007876") for
      // the cache equality check downstream.
      const decoded = decodeURIComponent(urlJobId);

      // Prefer the trailing token — that's where the new slug pattern places the ID.
      const trailing = decoded.split(/[-_]/).pop() || '';

      // JID + digits, anywhere in the slug (last-segment first, then full string).
      const jid = (trailing.match(/^jid\d+$/i) || decoded.match(/jid\d+/i) || [])[0];
      if (jid) return jid.toUpperCase();

      // Two-letter prefix + hyphen + digits (JB-…, MK-…, etc). Last segment alone
      // won't contain the hyphen, so check the trailing 2 tokens joined.
      const tail2 = decoded.split(/[-_]/).slice(-2).join('-');
      const dashed = (tail2.match(/^[a-z]{2,3}-\d+$/i) || decoded.match(/[a-z]{2,3}-\d+/i) || [])[0];
      if (dashed) {
        // Uppercase the alpha prefix; keep digits as-is.
        return dashed.replace(/^([a-z]{2,3})/i, (m) => m.toUpperCase());
      }

      // Pure-numeric ID anywhere (legacy fallback).
      if (/^\d+$/.test(trailing)) return trailing;
      if (/^\d+$/.test(decoded)) return decoded;

      console.warn('Could not extract job ID, returning original:', decoded);
      return decoded;

    } catch (error) {
      console.error('Error extracting job ID:', error);
      return urlJobId;
    }
  }

  private handleQueryParams() {
    // Subscribe to paramMap changes to track jobId changes from URL
    this.routeSub.add(
      this.activatedRoute.paramMap.subscribe(paramMap => {
        const rawJobId = paramMap.get('jobId');
        const newJobId = (!rawJobId || rawJobId === 'all') ? null : this.extractJobId(rawJobId);

        // Check if jobId changed
        if (newJobId !== this.jobId) {
          console.log('🔍 paramMap: Job ID changed from', this.jobId, 'to', newJobId);
          const oldJobId = this.jobId;
          this.jobId = newJobId;

          // DON'T update filterPayLoad.jobsToFilter here!
          // Let the queryParams handler manage the filter payload

          // Breadcrumb /jobs (and same-menu reclicks that produce the
          // /jobs/{slug} → /jobs URL transition) → user explicitly asked
          // for fresh data. The earlier version of this branch fell back
          // to `jobData.getCachedResults()` and just re-pointed the panes
          // at the first cached job, which left the listing stranded on
          // whatever the previous fetch returned (the reported "page
          // appears to reload but API isn't triggered, stale data"
          // symptom).
          //
          // Now: clear the singleton + selection, then go through
          // `resetFiltersOnMenuClick()` which resets the filter form,
          // search box, current page, and payload, and finally calls
          // `filterapicallSSR('')`. `handleResponse`'s auto-navigate-to-
          // first-job branch picks up the new first record once the
          // response arrives. paramMap emits BEFORE NavigationEnd /
          // queryParams in the router lifecycle, so by the time those
          // run, all the active-filter/search/page gates are false and
          // their own reset/search-clear branches stay no-op — no
          // duplicate API call.
          //
          // Discriminator vs. pagination/search: pagination navigates to
          // /jobs?page=N (strips the slug — same shape as breadcrumb), and
          // search carries ?searchText=… . Both ALSO set isSearching=true
          // before navigating. A genuine breadcrumb click hits bare /jobs
          // with zero query params. Without these gates this branch would
          // hijack pagination.
          const snapQp = this.activatedRoute.snapshot.queryParams;
          const isBreadcrumbNav = !this.isSearching && !snapQp['page'] && !snapQp['searchText'];
          if (newJobId === null && oldJobId && isBreadcrumbNav) {
            console.log('🔄 paramMap: /jobs breadcrumb → fresh API call (bypass in-memory cache)');
            this.selectedJobData = null;
            this.jobData.setData(null);
            this.resetFiltersOnMenuClick();
            return;
          }

          // Only make API call if:
          // 1. JobId changed to a new value (not just initialization)
          // 2. The job is NOT in our current results list (if it is, the card will handle it)
          // Fixed: Check if oldJobId is not null AND not undefined AND not empty string to avoid triggering on first card click
          if (oldJobId !== undefined && oldJobId !== null && oldJobId !== '') {
            console.log('🔍 paramMap: oldJobId has value, checking if job exists in cached results...');

            // Check cached results from shared service (persists across component recreations)
            const jobExistsInCache = this.jobData.jobExistsInCache(newJobId);

            // One-shot: a Similar Jobs widget click forces a fresh Job Details API
            // call even when the job is already cached. Consume the flag immediately.
            const forceRefresh = this.jobData.forceListingRefresh;
            this.jobData.forceListingRefresh = false;

            if ((!jobExistsInCache || forceRefresh) && newJobId) {
              // Pin the selected job to the top ONLY on the forward Similar-Jobs redirect
              // (forceRefresh) — that's the intended "show the selected job first" behavior.
              // A plain not-in-cache navigation doesn't pin (keeps the natural order).
              this.filterPayLoad.jobsToFilter = forceRefresh ? newJobId : null;
              this.filterapicallSSR(this.searchControl.value);
            } else {
              console.log('✅ paramMap: Job exists in cached results, skipping API call (card will handle selection)');
            }
          } else {
            console.log('✅ paramMap: Skipping API call on first navigation from generic listing page (oldJobId was null/undefined/empty)');
          }
        } else {
          console.log('⏭️ paramMap: Job ID unchanged, no action needed');
        }
      })
    );

    // Use a single subscription for ALL query parameter changes
    this.routeSub.add(
      this.activatedRoute.queryParams.subscribe(params => {
        console.log('Query params changed:', params);

        // "Find Job" menu click → URL navigated to a clean listing URL
        // (no path job, no `searchText`). Treat it as a fresh visit and
        // clear any filter-form state too. Filters live ONLY in form state
        // (engagementType, workMode, location, postedDayRange, experienceType)
        // — the URL never carries them — so without an explicit reset they
        // persist across navigations and the listing would still come back
        // filtered. Distinguished from a manual search-box clear, which
        // keeps the path job intact (so `menuSnapshotJobId` would be
        // non-null and this branch is skipped). isSearching guards
        // user-initiated searches out of this path. Returning early skips
        // the downstream branches — `resetFiltersOnMenuClick()` handles
        // search clear AND filter clear AND the API refetch in one go.
        const menuSnapshotRawJid = this.activatedRoute.snapshot.paramMap.get('jobId');
        const menuSnapshotJobId = (!menuSnapshotRawJid || menuSnapshotRawJid === 'all')
          ? null
          : this.extractJobId(menuSnapshotRawJid);
        // Pagination URLs (/jobs?page=N) also have no jobId / no searchText,
        // so the old check matched them. Real menu clicks never carry a
        // `page` query param; pagination always does. See the matching
        // guard in the NavigationEnd block above.
        const navPageRawForMenu = params['page'];
        const navPageForMenu = navPageRawForMenu ? parseInt(navPageRawForMenu, 10) : 1;
        const isCleanMenuUrl = !menuSnapshotJobId && !params['searchText'] && navPageForMenu <= 1;
        const hasActiveSearch = !!this.searchControl.value;
        // Filter detection MUST cover every control on `filterForm`, not just the
        // checkbox-count tallies. Radio-style filters (postedDayRange,
        // experienceType) and any non-empty location/jobClassification values
        // come in via `filterForm` directly and DO NOT update the *Count
        // fields. A narrow check missed those, leaving the user's "only
        // changed Posted Days" scenario falling through to the no-op path
        // — which is why the bug looked like the fix wasn't running.
        const f = this.filterForm?.value || {};
        const postedDayActive = f.postedDayRange && f.postedDayRange !== this.POSTED_ANY;
        const experienceActive = f.experienceType && f.experienceType !== this.EXPERIENCE_ANY;
        const locationActive = !!(f.country || f.state || f.city || f.zipCode);
        const classificationActive = Array.isArray(f.jobClassification) && f.jobClassification.length > 0;
        const hasActiveFilters =
          this.engagementTypeCount > 0 ||
          this.workTypeCount > 0 ||
          this.locationCount > 0 ||
          postedDayActive ||
          experienceActive ||
          locationActive ||
          classificationActive;
        // `currentPage > 1` covers the "paginated-but-no-filters" case
        // — see the matching NavigationEnd block above for rationale.
        if (isCleanMenuUrl && (hasActiveSearch || hasActiveFilters || this.currentPage > 1) && !this.isSearching) {
          console.log('🔄 Clean menu URL detected with active search/filters/page — resetting to default');
          this.resetFiltersOnMenuClick();
          return;
        }

        // Handle organization and business IDs
        if (params['orgId']) {
          this.filterPayLoad.organisationId = params['orgId'];
        }

        if (params['page']) {
          this.filterPayLoad.page = parseInt(params['page'], 10);
        } else {
          this.filterPayLoad.page = 1;
        }

        if (params['busId']) {
          this.filterPayLoad.businessId = params['busId'];
        }

        // Re-derive the active jobId from the snapshot rather than trusting
        // `this.jobId`. paramMap and queryParams are independent observables;
        // when the user clicks the "Find Job" menu from /jobs/some-jid?searchText=…
        // → /jobs, both URL params change at once. If this queryParams emission
        // arrives before paramMap has cleared `this.jobId`, we'd pin the stale
        // path job via `jobsToFilter` and the backend would keep it at the top
        // of the otherwise-default listing — that's the "searched job re-appears
        // at the top after clicking the Jobs menu" symptom. Snapshot is
        // authoritative regardless of emission order. Mirrors the candidate
        // listing's matching fix.
        const snapshotRawJobId = this.activatedRoute.snapshot.paramMap.get('jobId');
        const snapshotJobId = (!snapshotRawJobId || snapshotRawJobId === 'all')
          ? null
          : this.extractJobId(snapshotRawJobId);

        // IMPORTANT: only sync `this.jobId` and `jobsToFilter` from the URL
        // when this emission isn't part of an in-flight user search. The
        // search valueChanges tap deliberately sets `this.jobId = null` so
        // the auto-select gate in `handleResponse` opens onto the first
        // result of the new filtered set. The router.navigate it issues uses
        // `relativeTo: activatedRoute` + `queryParamsHandling: 'merge'`,
        // which preserves the OLD path-job in the URL — so the snapshot here
        // is stale during a search. Overwriting `this.jobId` with that stale
        // value re-closes the gate, which is why the detail panel kept
        // showing the previously-selected job after a fresh search.
        if (!this.isSearching) {
          this.jobId = snapshotJobId; // keep state in sync with URL truth

          // Only pin `jobsToFilter` when there's an active search context
          // OR this is the first emission (fresh navigation, e.g. from
          // home page clicking a card → /jobs/{slug}). For subsequent
          // emissions without `searchText`, the URL's leftover path job
          // should not anchor the listing (search-clear flow).
          const hasUrlSearchText = !!params['searchText'];
          if (hasUrlSearchText || this.isInitialQueryParams) {
            this.filterPayLoad.jobsToFilter = snapshotJobId;
          } else {
            this.filterPayLoad.jobsToFilter = null;
          }
        }
        // Flip the one-shot flag — every subsequent emission runs under
        // the existing searchText gate.
        this.isInitialQueryParams = false;

        // Similar Jobs widget click → always re-run the Job Details API for the new
        // job, even when it's cached. Handled HERE and in the paramMap branch so it
        // works whichever observable wins the emission race (one-shot flag consumed
        // by whichever fires first).
        if (this.jobData.forceListingRefresh && snapshotJobId && !this.isSearching) {
          this.jobData.forceListingRefresh = false;
          // Forward redirect from the Similar Jobs widget → pin the selected job to the top
          // (intended behavior). Back/Forward is handled by the NavigationEnd sync and does
          // NOT pin, so the original list order is restored on Back.
          this.filterPayLoad.jobsToFilter = snapshotJobId;
          this.filterapicallSSR(this.searchControl.value);
          return;
        }

        // Handle search text - but only if we're not in the middle of pagination
        if (params['searchText'] && !this.isSearching) {
          console.log('Handling search text from URL:', params['searchText']);

          if (this.searchControl.value !== params['searchText']) {
            const searchSub = this.valueChangesSub;
            this.valueChangesSub = new Subscription();

            this.searchControl.setValue(params['searchText']);

            const updatedPayload = {
              ...this.filterPayLoad,
              searchContent: params['searchText'],
              searchAfterKey: null,
              candidatesToFilter: null,
              jobsToFilter: snapshotJobId, // active search context — pin the URL job
            };
            this.filterPayLoad = updatedPayload;

            this.valueChangesSub = searchSub;
            this.filterapicallSSR(params['searchText']);
          }
        } else if (!params['searchText'] && this.searchControl.value && !this.isSearching) {
          // URL no longer carries `searchText` but the local search box still
          // holds a value — happens when the user clicks the "Find Job" menu
          // after a search. Clear the local state and reload the unfiltered
          // listing so the page returns to the default view.
          console.log('🔄 Clearing search state — searchText removed from URL');

          const searchSub = this.valueChangesSub;
          this.valueChangesSub = new Subscription();

          this.searchControl.setValue('');

          const updatedPayload = {
            ...this.filterPayLoad,
            searchContent: null,
            searchAfterKey: null,
            candidatesToFilter: null,
            jobsToFilter: null, // reset path — don't anchor on the prior search-era job
            page: 1,
          };
          this.filterPayLoad = updatedPayload;

          this.valueChangesSub = searchSub;
          this.filterapicallSSR('');
        }

        // Handle page changes - but only if not during a search
        if (params['page'] && !this.isSearching) {
          const newPage = parseInt(params['page'], 10);
          console.log('🔍 queryParams: page change check - newPage:', newPage, 'currentPage:', this.currentPage, 'Type:', typeof this.currentPage);

          // Check if API is already loading (from shared service to persist across component recreations)
          const isAlreadyLoading = this.jobData.isLoading;
          console.log('🔍 queryParams: isLoading status (from shared service):', isAlreadyLoading);

          if (newPage !== this.currentPage && newPage > 0) {
            console.log('📄 About to set currentPage from', this.currentPage, 'to', newPage);
            // Direction for browser back/forward + deep-link cases.
            const direction: 'forward' | 'backward' = newPage < this.currentPage ? 'backward' : 'forward';
            this.currentPage = newPage;
            console.log('📄 currentPage after assignment:', this.currentPage);
            this.updatePaginationConfig(direction);

            //  FIXED: Ensure job ID is preserved during pagination
            const updatedPayload = {
              ...this.filterPayLoad,
              page: this.currentPage,
              jobsToFilter: this.jobId
            };
            this.filterPayLoad = updatedPayload;

            // Only call API if not already loading (check shared service state)
            // (Component recreation during pagination means API is already in progress in the destroyed component)
            if (!isAlreadyLoading) {
              console.log('✅ Page actually changed and no API in progress, calling API');
              this.filterapicallSSR(this.searchControl.value);
            } else {
              console.log('⏭️ Skipping API call - API already in progress (from previous component instance during pagination)');
            }
          } else {
            console.log('⏭️ Skipping API call - page number unchanged or invalid');
          }
        } else {
          console.log('⏭️ Skipping page change handler - isSearching:', this.isSearching, 'page param:', params['page']);
        }

        // Update pagination config with current query params
        this.updatePaginationConfig();
      })
    );
  }

  // Custom pagination page change handler
  onPageChange(page: number): void {
    console.log('📄 Custom pagination page change:', page);
    console.log('📄 currentPage BEFORE:', this.currentPage);

    // Scroll the left listing column to the top of the new page. The
    // NavigationEnd handler only resets the detail/sidebar columns —
    // the left column is intentionally untouched there so the user's
    // position in the listing survives a card click. On pagination
    // though, the listing IS the thing changing, so resetting to top
    // is the right behaviour. Smooth so it doesn't jar.
    document.querySelector('.gj-jl-cards')?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

    // Capture direction BEFORE overwriting currentPage. Pagination strip
    // is destroyed and recreated during the loading state (the *ngIf
    // around <app-custom-pagination> flips on loadAPIcall), so direction
    // can't be tracked inside the child — has to ride on the config.
    const direction: 'forward' | 'backward' = page < this.currentPage ? 'backward' : 'forward';

    // Store current page FIRST
    this.currentPage = page;
    console.log('📄 currentPage AFTER:', this.currentPage);

    // Update payload with new page
    const updatedPayload = { ...this.filterPayLoad, page: this.currentPage, jobsToFilter: null };
    this.filterPayLoad = updatedPayload;

    // Set flag AFTER updating page to prevent interference
    this.isSearching = true;

    // Make API call with current search text
    this.filterapicallSSR(this.searchControl.value);

    // Update pagination config with direction so the recreated strip
    // picks the correct window bias.
    this.updatePaginationConfig(direction);

    // ✅ NAVIGATE TO GENERIC URL - Remove job-specific details from URL
    this.router.navigate(['/jobs'], {
      queryParams: {
        page: this.currentPage,
        // Preserve other query params but exclude job-specific ones
        ...Object.fromEntries(
          Object.entries(this.activatedRoute.snapshot.queryParams)
            .filter(([key]) => key !== 'page' && key !== 'jobId') // Remove page and jobId
        )
      },
      queryParamsHandling: 'merge', // Merge instead of replace to preserve other params
    }).then(() => {
      // Reset the searching flag after navigation completes
      setTimeout(() => {
        this.isSearching = false;
        console.log('Reset search flag after page change');
      }, 100); // Reduced timeout
    });

    // Update SEO elements with generic URL
    const canonicalUrl = `${environment.careOnlineWebUrl}jobs?page=${this.currentPage}`;
    this.siteMap.setCanonicalURL(canonicalUrl);
    this.siteMap.setPaginationLinks(this.currentPage);
  }

  // Check for SSR jobs data
  private checkForSSRJobsData(): boolean {
    if (this.transferState.hasKey(this.JOBS_LIST_KEY)) {
      const jobsData = this.transferState.get(this.JOBS_LIST_KEY, null);
      this.transferState.remove(this.JOBS_LIST_KEY);

      if (jobsData) {
        this.handleResponse({ code: GigsumoConstants.SUCESSCODE, data: jobsData }, '');
        this.isDataLoaded = true;
        this.loadAPIcall = false;
        return true;
      }
    }
    return false;
  }

  // Check for SSR countries data
  private checkForSSRCountriesData(): boolean {
    if (this.transferState.hasKey(this.COUNTRIES_KEY)) {
      const countriesData = this.transferState.get(this.COUNTRIES_KEY, null);
      this.transferState.remove(this.COUNTRIES_KEY);

      if (countriesData && countriesData.length > 0) {
        this.countryList = countriesData;
        return true;
      }
    }
    return false;
  }

  initializeFilterForm() {
    this.filterForm = new FormGroup({
      jobClassification: new FormControl([]),
      country: new FormControl(null),
      city: new FormControl(null),
      state: new FormControl(null),
      zipCode: new FormControl(null),
      postedDayRange: new FormControl(this.POSTED_ANY),
      experienceType: new FormControl(this.EXPERIENCE_ANY),
      experienceFrom: new FormControl(null),
      experienceTo: new FormControl(null),
      postedFrom: new FormControl(null),
      postedTo: new FormControl(null),
    });
  }

  // Keep existing scroll and filter methods...
  showChevron: boolean = false;
  showChevron1: boolean = false;
  onMouseEnter() { this.showChevron = true; }
  onMouseLeave() { this.showChevron = false; }
  onMouseEnter1() { this.showChevron1 = true; }
  onMouseLeave1() { this.showChevron1 = false; }

  scrollLeft() {
    if (isPlatformBrowser(this.platformId)) {
      const scrollContainer = document.getElementById('scrollContainer');
      if (scrollContainer) scrollContainer.scrollLeft -= 250;
    }
  }

  scrollRight() {
    if (isPlatformBrowser(this.platformId)) {
      const scrollContainer = document.getElementById('scrollContainer');
      if (scrollContainer) scrollContainer.scrollLeft += 250;
    }
  }

  loadAPIcall: boolean = false;
  // Set true when an API call has FINISHED successfully and `response` is
  // in its final post-API state. Reset to false at the start of every
  // `filterapicallSSR` so it toggles per-call — mirror of the candidate
  // listing's `apiCompleted`. The empty-state ngIf gates on this so a
  // stale `loadAPIcall=false` (e.g. from a late timer or interleaved
  // rapid-click sequence) can't uncover the empty state while a fresh
  // API is still in flight.
  apiCompleted: boolean = false;
  jobsFoundStatus: any = "Fetching Jobs...";
  jobModule!: jobModuleConfig;

  searchObserver = new BehaviorSubject<string>("");
  isFilterOpen: boolean = false;
  tempFilterPayload: any = {};
  filterPayLoad: any = {
    "source": {
      "marketPlaceJobs": true,
      "myJobs": false,
      "teamJobs": false,
      "recruiterNetworkJobs": false,
      "freeLancerNetworkJobs": false,
      "mtaNetworkJobs": false,
      "featured": false,
      "suggestedJobs": false,
      "businessNetwork": false,
      "memberNetworksJobs": false
    },
    "searchAfterKey": null,
    "workType": {
      "all": false,
      "remoteWork": false,
      "relocationRequired": false,
      "workFromHome": false
    },
    "experienceFrom": null,
    "experienceTo": null,
    "postedFrom": null,
    "postedTo": null,
    "country": null,
    "state": null,
    "city": null,
    "zipCode": null,
    "limit": null,
    "userId": "",
    "jobClassification": [],
    "clientType": [],
    "status": ["Active"],
    "searchContent": "",
    "organisationTypeParams": {
      "owner": false,
      "systemsIntergrator": false,
      "vendorStaffingAgency": false,
      "primeVendor": false
    },
    "businessNetworks": [],
    "customers": [],
    "businessId": ""
  };

  @Input() responseReceived: any;
  public response = new BehaviorSubject<any>(null);

  searchfilter(arg0: string) {
    // Empty implementation
  }

  /** Lightweight reset used by the queryParams "clean menu URL" branch when
   *  the user clicks "Find Job" with stale search/filters in form state.
   *  Differs from `resetFilternew()` (the Reset-button handler): doesn't
   *  navigate the URL (already on the menu URL) and skips the navigate
   *  feedback loop. Mirrors the candidate-listing helper of the same name. */
  private resetFiltersOnMenuClick(): void {
    this.filterForm.reset({
      postedDayRange: this.POSTED_ANY,
      experienceType: this.EXPERIENCE_ANY,
    });
    this.workModeCheckboxes?.forEach(x => x.selected = false);
    this.jobclasscificationCheckboxes?.forEach(x => x.selected = false);
    this.engagementTypeCount = 0;
    this.workTypeCount = 0;
    this.locationCount = 0;
    // The form reset clears country/state, but the State dropdown's options
    // are the last-picked country's; drop them so it isn't left offering
    // (and showing) that country's states with no country selected.
    this.stateList = [];

    const searchSub = this.valueChangesSub;
    this.valueChangesSub = new Subscription();
    this.searchControl.setValue('', { emitEvent: false });
    this.valueChangesSub = searchSub;

    this.jobId = null;
    this.currentPage = 1;
    this.updatePaginationConfig();

    this.filterPayLoad = structuredClone(this.tempFilterPayload);
    this.filterPayLoad.jobsToFilter = null;
    this.filterPayLoad.organisationId = null;
    this.filterPayLoad.businessId = null;
    this.filterPayLoad.searchContent = null;
    this.filterPayLoad.searchAfterKey = null;
    this.filterPayLoad.page = 1;

    this.filterapicallSSR('');
  }

  resetFilternew() {
    this.filterForm.reset({
      postedDayRange: this.POSTED_ANY,
      experienceType: this.EXPERIENCE_ANY,
    });

    if (this.workModeCheckboxes) {
      this.workModeCheckboxes.forEach(x => x.selected = false);
    }

    if (this.jobclasscificationCheckboxes) {
      this.jobclasscificationCheckboxes.forEach(x => x.selected = false);
    }

    this.engagementTypeCount = 0;
    this.workTypeCount = 0;
    this.locationCount = 0;
    // The form reset clears country/state, but the State dropdown's options
    // are the last-picked country's; drop them so it isn't left offering
    // (and showing) that country's states with no country selected.
    this.stateList = [];

    // Reset pagination to first page
    this.currentPage = 1;
    this.tempFilterPayload.page = 1;
    this.updatePaginationConfig();

    // Also clear the search box and drop `searchText` from the URL so the
    // listing returns to its default unfiltered state. The valueChanges pipe
    // is paused while we setValue so it doesn't re-fire and re-apply the
    // cleared value as a fresh search.
    const searchSub = this.valueChangesSub;
    this.valueChangesSub = new Subscription();
    this.searchControl.setValue('', { emitEvent: false });
    this.valueChangesSub = searchSub;
    this.jobId = null;

    this.filterPayLoad = structuredClone(this.tempFilterPayload);
    this.filterPayLoad.jobsToFilter = null;
    this.filterPayLoad.organisationId = null;
    this.filterPayLoad.businessId = null;
    this.filterPayLoad.searchContent = null;
    this.filterPayLoad.searchAfterKey = null;

    // Navigate to the bare /jobs URL so the address bar reflects the
    // default state. Absolute path here — NOT `relativeTo: this.activatedRoute`
    // — because the user is typically on `/jobs/{searched-slug}?searchText=foo`
    // when they click Reset, and a relative navigate would keep the path
    // slug and only strip the query params. That left the snapshot
    // showing the old path-job, which the unconditional snapshot-sync
    // block in `handleQueryParams`' queryParams subscribe (added in
    // Round 16 for the menu-click race) then used to flip `this.jobId`
    // back from null to the old slug id. By the time the API response
    // arrived, `handleResponse`'s `if (!this.jobId)` gate was closed and
    // the auto-navigate-to-first-job branch was skipped — the user saw
    // the listing refresh but no first card got selected. Absolute `/jobs`
    // makes the snapshot reflect null jobId, so the sync is a no-op and
    // the auto-select fires correctly. (The candidate side doesn't have
    // the unconditional sync, which is why /talents Reset has always
    // worked.)
    const { searchText, page, ...keepParams } = this.activatedRoute.snapshot.queryParams as any;
    this.router.navigate(['/jobs'], {
      queryParams: keepParams,
    });

    this.filterapicallSSR('');
  }

  openFilterSlider() {
    // Empty implementation
  }

  ngOnDestroy() {
    this.response.complete();
    if (this.valueChangesSub) {
      this.valueChangesSub.unsubscribe();
    }
    if (this.FilterSubscription) {
      this.FilterSubscription.unsubscribe();
    }
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  FilterSubscription: Subscription = new Subscription;
  businessId: any;
  SUCCESS_CODE: string = GigsumoConstants.SUCESSCODE;
  API_NAME: string = 'jobs/findJobsForOpenSession';
  valueChangesSub = new Subscription;

  // Updated API call method to use DataTransferService
  filterapicallSSR(searchContent: any = '') {
    this.loadAPIcall = true;
    this.apiCompleted = false; // toggle off — gates the empty-state ngIf
    this.jobData.setLoadingState(true); // Track in shared service
    const updatedPayload = { ...this.filterPayLoad, searchContent: searchContent };
    this.filterPayLoad = updatedPayload;

    // Reset the left-column scroll on every "fresh listing data" fetch
    // (search, filter, reset, pagination, page-reload). Card clicks hit
    // the cache and don't reach here, so the user's list position is
    // preserved when switching between cards. Guarded by isPlatformBrowser
    // because this method also runs server-side during SSR.
    if (isPlatformBrowser(this.platformId)) {
      document.querySelector('.gj-jl-cards')?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }

    if (this.filterPayLoad) {
      this.response.next([]);
    }

    // Cancel any in-flight request before starting a new one. On rapid
    // searches, the first response would otherwise run `handleResponse`,
    // set `this.jobId` via auto-navigate, and then the second response
    // would hit `JobsListdata` with `shouldAutoSelect=false` — so the
    // first card of the newer results never becomes the active one and
    // the detail panel stays on the older first result.
    if (this.FilterSubscription) {
      this.FilterSubscription.unsubscribe();
    }

    // Use the DataTransferService instead of direct API call
    this.FilterSubscription = this.dataTransfer.postData<any>(
      this.API_NAME,
      this.filterPayLoad,
      'jobs_listing_' + this.currentPage
    ).subscribe({
      next: (res) => this.handleResponse(res, searchContent),
      error: (err) => this.handleError(err)
    });
  }

  handleResponse(res: { code: any; data: any; }, searchContent: string) {
    if (res.code === this.SUCCESS_CODE) {
      // Update `response` (via JobsListdata) BEFORE flipping `loadAPIcall`
      // off below. Previously `loadAPIcall = false` ran first, leaving a
      // synchronous window where the template's empty-state ngIf
      // (`!loadAPIcall && response.length===0`) could paint between the
      // flag flip and the response update — visible as a random
      // empty-state flash on rapid breadcrumb/footer clicks.
      this.JobsListdata(res.data, searchContent);
      this.apiCompleted = true;

      // If we're on a generic listing page (no jobId in URL) and have results,
      // immediately navigate to the first job to avoid route definition mismatch.
      // BUT never during a browser Back/Forward (popstate) — on Back we must honor the
      // URL the user is returning to, not jump to the first job (that hijacks Back).
      if (!this.jobId && !this.isPopStateNav && res.data?.jobList && res.data.jobList.length > 0) {
        const firstJob = Array.isArray(res.data.jobList) ? res.data.jobList[0] : null;
        if (firstJob) {
          // Set the jobId immediately BEFORE navigation so cards can highlight correctly
          const extractedId = firstJob.jobId || firstJob.id;
          this.jobId = extractedId;
          console.log('🔄 Setting jobId before navigation:', this.jobId);

          const firstJobUrl = this.generateJobUrl(firstJob);
          console.log('🔄 Auto-navigating to first job on page:', firstJobUrl);

          // Navigate to specific job URL while preserving query params.
          // replaceUrl:true is CRITICAL for the browser Back button: this is an
          // automatic redirect (generic /jobs → first job), not a user action, so it
          // must REPLACE the current history entry instead of pushing a new one.
          // Otherwise the bare "/jobs" entry stays in history and Back into it
          // auto-navigates forward to the first job again — a Back trap — which also
          // fires competing navigations that desync the URL from the shown job.
          this.router.navigate([firstJobUrl], {
            queryParams: this.activatedRoute.snapshot.queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
      }
    }
    // Flip loading flags LAST so the template never observes
    // `loadAPIcall=false && response=[]` between the loading flip and
    // the response update.
    this.loadAPIcall = false;
    this.jobData.setLoadingState(false);
  }

  handleError(err: any) {
    this.loadAPIcall = false;
    this.jobData.setLoadingState(false); // Clear loading state in shared service
    console.error('Error fetching jobs:', err);
  }

  stopscrollFlag: boolean = false;
  totalCountJob: number = 0;
  loadCount: number = 0;

  JobsListdata(data: { jobList: string | any[]; searchAfterKey: string; total: number; }, searchContent: string) {
    let content = this.filterPayLoad ? this.filterPayLoad.searchContent : null;
    this.loadCount = this.loadCount + 1;

    // Check if the search conditions are met
    if (this.filterPayLoad && this.filterPayLoad.searchAfterKey === null ||
      (content != null && searchContent != null && (content && content.trim()) === (searchContent && searchContent.trim()) && searchContent.length > 0)) {
      this.stopscrollFlag = false;
      this.response.next([]);
    }

    // If new job list is received
    if (data.jobList && data.jobList.length > 0) {
      const res = data.jobList;
      this.response.next(res);

      // Cache results in shared service (persists across component recreations)
      this.jobData.setCachedResults(Array.isArray(res) ? res : []);

      //  IMPROVED: Better job selection logic
      if (this.filterPayLoad && this.filterPayLoad.searchAfterKey === null && Array.isArray(res) && res.length > 0) {
        // Auto-select if:
        // 1. No current job data exists, OR
        // 2. We're on a generic listing page (no specific jobId in URL), OR
        // 3. Current page doesn't match the job in current data
        const currentJobData = this.jobData.getJob;
        const isGenericListingPage = !this.jobId;
        // Provider out of sync with the URL job → Back/Forward navigation. Re-select so the
        // detail matches the address bar (the provider is only updated by forward clicks).
        const urlMismatch = !!this.jobId && !!currentJobData?.jobId &&
          currentJobData.jobId.toString().toUpperCase() !== this.jobId.toString().toUpperCase();
        const shouldAutoSelect = !currentJobData || !currentJobData.jobId || isGenericListingPage || urlMismatch;

        if (shouldAutoSelect) {
          // If we have a specific job ID, try to find and select that job
          if (this.jobId) {
            const specificJob = Array.isArray(res)
              ? res.find((job: any) =>
                job.jobId === this.jobId ||
                job.id === this.jobId ||
                job.jobId?.toString() === this.jobId?.toString()
              )
              : undefined;

            if (specificJob) {
              console.log('Found specific job:', specificJob);
              this.selectedJobData = specificJob;
              this.jobDetailsTabName = "job-details";
              this.jobData.setData(this.selectedJobData);
            } else {
              console.warn('Specific job not found in results, keeping pre-seeded jobData; falling back selectedJobData to first job for internal/highlight tracking only');
              // selectedJobData drives the in-listing highlight; jobData
              // drives the detail panel. When the URL JID isn't in the
              // returned page (e.g. arriving from the home dropdown,
              // where the listing API ignores the click context), the
              // pre-seeded jobData from the click site is the correct
              // detail. Do NOT overwrite it with res[0].
              this.selectedJobData = Array.isArray(res) ? res[0] : undefined;
              this.jobDetailsTabName = "job-details";
            }
          } else {
            // No specific job ID, use first job
            this.selectedJobData = Array.isArray(res) ? res[0] : undefined;
            this.jobDetailsTabName = "job-details";
            this.jobData.setData(this.selectedJobData);
          }
        } else {
          console.log('Job data already set, skipping auto-select to prevent redundant updates');
          // Still update selectedJobData for internal consistency, but don't call setData again
          if (this.jobId) {
            const specificJob = res.find((job: any) =>
              job.jobId === this.jobId ||
              job.id === this.jobId ||
              job.jobId?.toString() === this.jobId?.toString()
            );
            if (specificJob) {
              this.selectedJobData = specificJob;
            }
          }
        }
      }
    }
    else if (this.response.getValue()?.length == 0 || !data.jobList || data.jobList.length === 0) {
      // If job list is empty
      this.totalCountJob = 0;
      this.jobData.setData(null);
    }

    // Update total job count and pagination config
    if (data.total !== undefined || data.total === 0) {
      this.totalCountJob = data.total;
      this.updatePaginationConfig();
    }
  }

  isParenActivity(data: string): boolean {
    return data === 'job-applicants' || data === 'candidate-applied' || data === 'candidates-invited';
  }

  jobDetailsTabName: string = "job-details";
  selectedJobData!: any;

  updateJobPortfolioDetails(data: string) {
    this.jobDetailsTabName = data;
    if (!this.isParenActivity(data)) {
      this.selectedJobData = { ...this.selectedJobData };
      this.selectedJobData.call = false;
    } else {
      this.selectedJobData = { ...this.selectedJobData };
    }
  }

  POSTED_ANY = GigsumoConstants.POSTED_ANY;
  POSTED_TODAY = GigsumoConstants.POSTED_TODAY;
  POSTED_LAST_7_DAYS = GigsumoConstants.POSTED_LAST_7_DAYS;
  POSTED_LAST_14_DAYS = GigsumoConstants.POSTED_LAST_14_DAYS;
  POSTED_LAST_30_DAYS = GigsumoConstants.POSTED_LAST_30_DAYS;

  onPostedDaysChange(content: string) {
    if (content === GigsumoConstants.POSTED_TODAY) {
      this.filterForm.patchValue({ postedFrom: 1, postedTo: 1 });
    } else if (content === GigsumoConstants.POSTED_LAST_7_DAYS) {
      this.filterForm.patchValue({ postedFrom: 1, postedTo: 7 });
    } else if (content === GigsumoConstants.POSTED_LAST_14_DAYS) {
      this.filterForm.patchValue({ postedFrom: 1, postedTo: 14 });
    } else if (content === GigsumoConstants.POSTED_LAST_30_DAYS) {
      this.filterForm.patchValue({ postedFrom: 1, postedTo: 30 });
    } else {
      this.filterForm.patchValue({ postedFrom: null, postedTo: null });
    }

    this.updateFunction();
  }

  EXPERIENCE_ANY = GigsumoConstants.EXPERIENCE_ANY;
  EXPERIENCE_JUNIOR = GigsumoConstants.EXPERIENCE_JUNIOR;
  EXPERIENCE_MID_LEVEL = GigsumoConstants.EXPERIENCE_MID_LEVEL;
  EXPERIENCE_SENIOR = GigsumoConstants.EXPERIENCE_SENIOR;
  EXPERIENCE_ARCHITECT = GigsumoConstants.EXPERIENCE_ARCHITECT;
  engagementTypeCount: number = 0;

  jobclasscificationCheckboxes = [
    { label: 'All', selected: false, disable: false },
    { label: 'Corp To Corp', selected: false, disable: false },
    { label: 'W2 - Contract', selected: false, disable: false },
    { label: 'W2 - Full Time', selected: false, disable: false },
    { label: 'Part Time', selected: false, disable: false },
    { label: 'Freelance', selected: false, disable: false },
    { label: 'Internship', selected: false, disable: false },
    { label: 'Contract to Hire', selected: false, disable: false },
    { label: 'Direct Hire', selected: false, disable: false },
    { label: 'Other', selected: false, disable: false },
  ];

  onExperienceValueChange(content: any) {
    if (content === GigsumoConstants.EXPERIENCE_JUNIOR) {
      this.filterForm.patchValue({ experienceFrom: 1, experienceTo: 3 });
    } else if (content === GigsumoConstants.EXPERIENCE_MID_LEVEL) {
      this.filterForm.patchValue({ experienceFrom: 4, experienceTo: 6 });
    } else if (content === GigsumoConstants.EXPERIENCE_SENIOR) {
      this.filterForm.patchValue({ experienceFrom: 6, experienceTo: 10 });
    } else if (content === GigsumoConstants.EXPERIENCE_ARCHITECT) {
      this.filterForm.patchValue({ experienceFrom: 10, experienceTo: 20 });
    } else {
      this.filterForm.patchValue({ experienceFrom: null, experienceTo: null });
    }
    this.updateFunction();
  }

  jobclassficheckbox(dv: any, { target: { checked } }: any) {
    if (dv === 'All') {
      this.jobclasscificationCheckboxes.forEach(x => x.selected = checked ? true : false);
    }
    else {
      const ifAllChecked = this.jobclasscificationCheckboxes.filter(x => x.label != 'All').every(c => c.selected === true);
      this.jobclasscificationCheckboxes[0].selected = ifAllChecked ? true : false;
    }

    const d = this.jobclasscificationCheckboxes.filter(x => (x.selected && x.label != 'All')).map(x => x.label);
    this.filterForm.get('jobClassification')?.patchValue(d);
    this.engagementTypeCount = d != null ? d.length : 0;
    this.updateFunction();
  }

  workTypeCount: number = 0;
  workTypecheckbox({ target: { checked } }: any, label?: string) {
    const workTypeForm = this.filterForm.get('workType') as FormGroup;

    if (label === 'all') {
      this.checkAllWorkType(workTypeForm, checked ? true : false);
    }
    else {
      const ifAllChecked = Object.keys(workTypeForm.controls).filter(x => x != 'all').every(c => workTypeForm.get(c)?.value === true);
      workTypeForm.get('all')?.patchValue(ifAllChecked ? true : false);
    }

    this.workTypeCount = Object.values((this.filterForm.get('workType') as FormGroup).controls).map(x => x.value).
      reduce((acc, curr, index) => {
        if (index === 0) return acc;
        return curr === true ? acc + 1 : acc;
      }, 0);

    this.updateFunction();
  }

  workModeChange({ checked }: any, label: string) {
    if (label === 'All') {
      if (checked) {
        this.workModeCheckboxes.forEach(item => item.selected = true);
      }
      else if (!checked) {
        this.workModeCheckboxes.forEach(item => item.selected = false);
      }
    }
    else if (label != 'All') {
      this.workModeCheckboxes.forEach(item => {
        if (item.label === label) {
          item.selected = checked;
        }
      });

      const isAllchecked = this.workModeCheckboxes.filter(val => val.label != 'All').every(item => item.selected === true);
      this.workModeCheckboxes[0].selected = isAllchecked;
    }

    this.workTypeCount = this.workModeCheckboxes.filter(x => (x.label != 'All' && x.selected)).length;
    this.updateFunction();
  }

  checkAllWorkType(form: FormGroup, val: boolean) {
    Object.keys(form.controls).forEach(x => {
      form.get(x)?.patchValue(val);
    });
  }

  updateFunction() {
    const data = this.filterForm.getRawValue();
    data.workMode = this.workModeCheckboxes ? this.workModeCheckboxes.filter(x => x.selected).map(c => c.label) : [];
    this.resetFilterPayload();

    this.filterPayLoad = {
      ...this.filterPayLoad,
      ...data
    };

    this.filterapicallSSR(this.searchControl.value);
  }

  resetFilterPayload() {
    // Filter changes must start from a clean slate:
    //  • `jobsToFilter` previously preserved the currently-viewed job ID,
    //    which forced the backend to keep that job in the response even
    //    when it didn't match the new filter — that's why "upgraded" /
    //    previously-viewed records were leaking through.
    //  • `searchAfterKey` carries the cursor from the last paginated
    //    request; leaving it set means the new filtered query starts
    //    mid-stream and skips matching records at the top.
    //  • Clearing `jobId` opens the auto-select gate in handleResponse so
    //    the first record of the new filtered set becomes the active one
    //    (same pattern as the search-clear path).
    this.filterPayLoad.jobsToFilter = null;
    this.filterPayLoad.searchAfterKey = null;
    this.filterPayLoad.page = 1;

    this.jobId = null;

    this.currentPage = 1;
    this.updatePaginationConfig();
  }

  // Updated to use DataTransferService
  getCountries() {
    this.countryList = [];
    this.dataTransfer.fetchData<any>(
      "country/getAllCountries",
      'countries_list'
    ).subscribe({
      next: (res) => {
        if (res != undefined) {
          res.forEach((ele: any) => {
            this.countryList.push(ele);
          });
        }
      },
      error: (err) => {
        console.error('Error fetching countries:', err);
      }
    });
  }

  updateLocationCount() {
    const { city, zipCode, state, country } = this.filterForm.value;
    console.log(city, zipCode, state, country);

    this.locationCount = (city || zipCode || state || country) ? 1 : 0;
  }

  onChangeCountry() {
    const countryCode = this.filterForm.value.country;
    this.filterForm.get('city')?.patchValue(null);
    this.filterForm.get('zipCode')?.patchValue(null);
    this.filterForm.get('state')?.patchValue(null);

    this.updateLocationCount();

    if (countryCode === "US") {
      this.stateList = [];
      this.filterForm.get('city')?.patchValue(null);
      this.filterForm.get('zipCode')?.patchValue(null);
      this.filterForm.get('state')?.patchValue(null);
      this.updateFunction();
      return;
    }

    this.updateFunction();
    this.stateList = [];

    // Use DataTransferService for states
    this.dataTransfer.fetchData<any>(
      "country/getAllStates?countryCode=" + countryCode,
      'states_' + countryCode
    ).subscribe({
      next: (res) => {
        // Reset (or another country pick) landed while this was in flight —
        // don't refill the State dropdown with a country no longer selected.
        if (this.filterForm.value.country !== countryCode) {
          return;
        }
        if (res && res.length > 0) {
          this.stateList = res;
        } else {
          this.stateList = [];
        }
      },
      error: (err) => {
        console.error('Error fetching states:', err);
        this.stateList = [];
      }
    });

    this.updateFunction();
  }

  onChangestate = customDebounceTime(() => {
    this.filterForm.get('city')?.patchValue(null);
    this.filterForm.get('zipCode')?.patchValue(null);
    this.updateLocationCount();
    this.updateFunction();
  }, 400);

  onChangecity = customDebounceTime(() => {
    this.filterForm.get('zipCode')?.patchValue(null);
    this.updateLocationCount();
    this.updateFunction();
  }, 400);

  onChangeZipCode = customDebounceTime((e: any) => {
    this.updateLocationCount();
    e.target.value = e.target.value.replace(/\D/g, '');

    const val = this.filterForm.get('zipCode')?.value;
    if (val && val.length > 3) {
      this.updateFunction();
    }
    else if (val && val.length === 0) {
      this.updateFunction();
    }
  }, 400);

  onKeyZip(event: any) {
    this.updateLocationCount();
    let data: any = {};
    data.countryCode = "US";
    data.zipCode = event.target.value;
    data.stateCode = "";

    if (data.zipCode.length > 4) {
      // Use DataTransferService for geodetails
      this.dataTransfer.postData<any>(
        "country/geodetails",
        data,
        'geodetails_' + data.zipCode
      ).subscribe({
        next: (res) => {
          if (res.length === 0) {
            this.filterForm.get('city')?.patchValue(null);
            this.filterForm.get('state')?.patchValue(null);
            this.updateFunction();
            return;
          }

          if (
            res &&
            res != null &&
            res != "" &&
            res.length > 0 &&
            event.target.value != ""
          ) {
            res.forEach((ele: any) => {
              let cityName = ele.cityName;
              let stateName = ele.stateName;
              this.filterForm.patchValue({
                city: cityName,
                state: stateName,
              });
            });
            this.updateFunction();
          }
        },
        error: (err) => {
          console.error('Error fetching geodetails:', err);
        }
      });
    }
  }

  digitKeyOnly(e: any) {
    return digitKeyOnly(e);
  }

  // New design: login redirect for the mobile apply bar CTA
  getLoginParams(job: any) {
    return getJobParams(job);
  }
}